/**
 * Тема оформления — личная настройка, хранимая на сервере.
 *
 * В браузере тема тоже лежит (tf.theme): по ней index.html красит страницу
 * до первой отрисовки, без вспышки светлой темы. Сервер же делает выбор
 * общим для всех устройств человека. Правило согласования:
 *   * при входе действует выбор с сервера, если он есть;
 *   * если на сервере выбора нет — остаётся тема браузера, ничего не пишется;
 *   * любая смена темы после этого (профиль, кнопка в шапке) уходит на сервер;
 *   * смена на другом устройстве (приложение, вторая вкладка) приходит
 *     событием profile.updated и применяется без перезагрузки.
 */

import { useEffect, useRef } from "react";

import { useUiPrefsQuery, useUpdateUiPrefsMutation } from "@taskflow/client-core/profile";

import { useAppDispatch, useAppSelector } from "./hooks";
import type { Theme } from "@taskflow/client-core";
import { themeChanged } from "./uiSlice";

export function useThemeSync(): void {
  const theme = useAppSelector((state) => state.ui.theme);
  const dispatch = useAppDispatch();
  const prefs = useUiPrefsQuery();
  const [update] = useUpdateUiPrefsMutation();
  /** Тема, о которой сервер уже знает; undefined — настройки ещё не пришли. */
  const synced = useRef<Theme | null | undefined>(undefined);

  const stored = prefs.data?.theme;

  useEffect(() => {
    if (stored === undefined) return;
    if (synced.current === undefined) {
      synced.current = stored;
      if (stored !== null && stored !== theme) dispatch(themeChanged(stored));
      return;
    }
    // Сервер сообщил другую тему — её выбрали на другом устройстве.
    if (stored !== null && stored !== synced.current) {
      synced.current = stored;
      if (stored !== theme) dispatch(themeChanged(stored));
      return;
    }
    if (theme !== synced.current) {
      synced.current = theme;
      void update({ theme });
    }
  }, [dispatch, stored, theme, update]);
}
